import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getStyles, translate, addModifierClasses, addToArray } from '../../../shared/utility';

import Icon from '../Icon/Icon';

import localStyles from './Dropdown.scss';
import inputStyles from '../Input/Input.scss';
const styles = getStyles({ ...localStyles, ...inputStyles });

class Dropdown extends PureComponent {
    /* * * * * * * * *
     * REACT METHODS *
     * * * * * * * * */
    constructor(props) {
        super(props);
        this.state = {
            isOpen: props.isOpen ? true : false,
            keyboardFocusIndex: -1,
            options: [],
            originalOptions: [],
            selected: []
        };
        this.refContainer = React.createRef();
        this.refList = React.createRef();

        this.searchBuffer = '';
        this.searchBufferTimeout = null;
    }

    componentDidMount() {
        if(!this.props.dropdownOnly){
            document.addEventListener('click', this.documentOnClick);
        }
    }

    componentWillUnmount() {
        if(!this.props.dropdownOnly){
            document.removeEventListener('click', this.documentOnClick);
        }
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const newState = {};
        if (!prevState || nextProps.options !== prevState.originalOptions) {
            const options = (nextProps.options || []).map((option, index) => ({ id: index, ...option }));
            newState.originalOptions = nextProps.options;
            newState.options = options;
            newState.hasIcons = options.reduce((hasIcon, option) => (option.icon || option.customIcon || hasIcon), false);
            newState.selected = options.filter(option => option.selected).map(option => option.id);
        }
        if (!prevState || nextProps.max !== prevState.max) {
            newState.max = parseInt(nextProps.max, 10) || 1;
        }
        if (nextProps.isOpen !== undefined) {
            newState.isOpen = nextProps.isOpen ? true : false;
        }
        return newState;
    }

    /* * * * * * * * * *
     * EVENT HANDLERS  *
     * * * * * * * * * */
    documentOnClick = (event) => {
        if (!!this.refContainer && !!this.refContainer.current && !this.refContainer.current.contains(event.target)) {
            this.handleOpenClose(false);
        }
    }

    handleOpenClose = (open) => {
        const isOpen = typeof open === 'boolean' ? open : !this.state.isOpen;
        const newState = {
            isOpen: isOpen,
            keyboardFocusIndex: isOpen && this.state.selected.length ? this.getKeyboardFocusIndex() : isOpen ? 0 : this.state.keyboardFocusIndex
        };
        
        if(isOpen && newState.keyboardFocusIndex > -1) {
            this.setListScrollTop(newState.keyboardFocusIndex, true);
        }

        if(Object.keys(newState).length){
            this.setState(newState);
        }
    }

    handleSelection = (clickedOption) => {
        const multiple = this.state.max > 1;
        let currentlySelected = this.state.selected.length;

        const updatedOptions = this.state.options.slice().map((option) => {
            if (!multiple) {
                option.selected = (option.id === clickedOption.id);
            } else if (option.id === clickedOption.id) {
                if (option.selected) {
                    option.selected = false;
                    currentlySelected--;
                } else if (currentlySelected < this.state.max) {
                    option.selected = true;
                    currentlySelected++;
                }
            }
            return option;
        });

        const updatedSelected = updatedOptions.filter(option => option.selected).map(option => option.id);

        if (this.props.callback && typeof this.props.callback === 'function') {
            this.props.callback(updatedOptions.filter(option => option.selected).map(option => option.value));
        }

        if (!multiple) {
            this.handleOpenClose(false);
        }

        this.setState({
            options: updatedOptions,
            selected: updatedSelected
        });
    }

    /* * * * * * * * * *
     * KEYBOARD EVENTS *
     * * * * * * * * * */
    // Events get bound after the dropdown has gain focus.
    // Key bindings per state:
    // -----------------|------------------|------------------|--------------
    //  KEY             | DROPDOWN OPENED  | DROPDOWN CLOSED  | IMPLEMENTED
    // -----------------|------------------|------------------|--------------
    //  ENTER           | SELECT VALUE     | DEFAULT          | YES
    //  SPACE           | SELECT VALUE     | OPEN DROPDOWN    | YES
    //  TAB             | IGNORE           | DEFAULT          | YES
    //  ESCAPE          | DEFAULT          | CLOSE DROPDOWN   | YES
    //  ARROW UP        | HILIGHT PREV VAL | OPEN DROPDOWN    | YES
    //  ARROW DOWN      | HILIGHT NEXT VAL | OPEN DROPDOWN    | YES
    //  /^[\S ]{1}$/    | SEARCH & SELECT  | SEARCH           | YES

    bindKeyboardEvents = () => {
        document.addEventListener('keydown', this.keyboardListener);
    }

    unbindKeyboardEvents = () => {
        document.removeEventListener('keydown', this.keyboardListener);
    }

    keyboardListener = (event) => {
        const key = event.key;
        if (['Enter', ' ', 'Escape', 'ArrowUp', 'ArrowDown', 'Tab'].includes(key)) {
            if (this.state.isOpen) {
                switch (key) {
                    case 'Enter':
                    case ' ':
                        if (key !== ' ' || !this.searchBuffer.length) {
                            event.preventDefault();
                            const focussedElement = this.state.options[this.state.keyboardFocusIndex];
                            if (focussedElement) {
                                this.handleSelection(focussedElement);
                            }
                        }
                        break;
                    case 'ArrowUp':
                        event.preventDefault();
                        this.setKeyboardFocus(this.state.keyboardFocusIndex - 1, true);
                        break;
                    case 'ArrowDown':
                        event.preventDefault();
                        this.setKeyboardFocus(this.state.keyboardFocusIndex + 1, true);
                        break;
                    case 'Escape':
                        event.preventDefault();
                        this.handleOpenClose(false);
                        break;
                    case 'Tab':
                        event.preventDefault();
                        break;
                    default:
                        break;
                }
            } else {
                switch (key) {
                    case 'ArrowUp':
                    case 'ArrowDown':
                    case ' ':
                        if (key !== ' ' || !this.searchBuffer.length) {
                            event.preventDefault();
                            this.handleOpenClose(true);
                        }
                        break;
                    default:
                        break;
                }
            }
        }

        if (/^[\S ]{1}$/.test(key)) {
            event.preventDefault();
            this.addToSearchBuffer(key);
        }
    }

    setKeyboardFocus = (index, scroll = true) => {
        const newIndex = Math.max(0, Math.min(index, this.state.options.length - 1));
        if (newIndex !== this.state.keyboardFocusIndex) {
            if (scroll) {
                this.setListScrollTop(newIndex);
            }
            this.setState({ keyboardFocusIndex: newIndex });
        }
    }

    getKeyboardFocusIndex = () => Math.max(0, this.state.options.findIndex(option => option.selected));

    setListScrollTop = (index, itemOnTop) => {
        const focusElement = this.refList.current.children[index];
        if (!focusElement) {
            return;
        }
        const height = focusElement.offsetHeight;
        const top = focusElement.offsetTop;
        const listHeight = this.refList.current.offsetHeight;
        const scrollTop = this.refList.current.scrollTop;
        const offset = top + height - scrollTop;
        if (offset < 0.5 * height || itemOnTop) {
            this.refList.current.scrollTop = top;
        } else if (offset > listHeight + 0.5 * height) {
            this.refList.current.scrollTop = top + height - listHeight;
        }
    }

    /* * * * * *
     * SEARCH  *
     * * * * * */
    addToSearchBuffer = (letter) => {
        if (this.searchBufferTimeout !== null) {
            clearTimeout(this.searchBufferTimeout);
            this.searchBufferTimeout = null;
        }
        this.searchBuffer += letter;
        this.searchBufferTimeout = setTimeout(() => this.searchBuffer = '', 500);
        const index = this.findIndexBySearchString(this.searchBuffer);
        if (index > -1) {
            if (this.state.isOpen) {
                this.setKeyboardFocus(index, true);
            } else {
                this.handleSelection(this.state.options[index]);
            }
        }
    };

    findIndexBySearchString = (searchString) => {
        const search = searchString.toLowerCase();
        return this.state.options.findIndex(option => option.text.toLowerCase().substr(0, search.length) === search || option.value.toLowerCase().substr(0, search.length) === search);
    };

    /* * * * * * * * * *
     * CUSTOM METHODS  *
     * * * * * * * * * */
    hasIcons = (options) => options.reduce((hasIcon, option) => (option.icon || option.customIcon || hasIcon), false);

    getDropdownValue = () => this.state.options.filter(option => option.selected).map(option => (option.text || option.value)).join(this.props.separator || ', ') || this.props.defaultValue || translate('ui.select', this.props.language);

    getDropdownDescription = () => this.state.options.filter(option => option.selected && !!option.description).length ? this.state.options.filter(option => option.selected).find(option => !!option.description).description : null;

    getDropdownIcon = (which = 'icon') => ((this.state.selected.length && this.state.max > 1) || !this.state.selected.length) && this.props.defaultIcon ? this.props.defaultIcon : (this.state.options.find(option => option.selected) || {})[which];

    /* * * * * *
     * RENDER  *
     * * * * * */
    render() {
        const dropdownValue = this.getDropdownValue();
        const dropdownIcon = this.getDropdownIcon();
        const dropdownDescription = this.getDropdownDescription();
        const customDropdownIcon = dropdownIcon ? null : this.getDropdownIcon('customIcon');
        
        let description = null;
        if (this.props.description) {
            description = <p className={styles('tiny', 'c--text-light', 'description')}>{this.props.description}</p>;
        }

        const inputClasses = addModifierClasses(
            (this.props.inputModifiers || []).concat(description ? 'collapsed' : ''),
            'input',
            ['input']
        );

        let ddClasses = ['flex', 'flex--align-center'].concat(inputClasses)
            .concat('dropdown__input')
            .concat(this.state.isOpen ? 'dropdown__input--open' : '');

        ddClasses = addToArray(this.props.classes, ddClasses);

        const optionsModifiers = []
            .concat(this.props.collapsed ? 'collapsed' : '')
            .concat(this.state.isOpen ? 'open' : '')
            .concat(this.props.isSelect ? 'select' : '');
        const optionsClasses = addModifierClasses(optionsModifiers, 'options', 'options');

        const optionItemModifiers = []
            .concat(this.props.collapsed ? 'collapsed' : '')
            .concat(this.props.isSelect ? 'select' : '');
        let optionItemClasses = addModifierClasses(optionItemModifiers, 'options__item', 'options__item');

        if (!this.props.collapsed) {
            optionItemClasses = addToArray(['flex', 'flex--align-center'], optionItemClasses);
        }

        const optionsArrowModifiers = []
            .concat(this.props.isSelect ? 'select' : '');
        const optionsArrowClasses = addModifierClasses(optionsArrowModifiers, 'options__arrow', 'options__arrow');


        let selectElement = null;
        if (this.props.isSelect) {
            const multiple = this.state.max > 1;
            const attrs = {};
            if (!multiple) {
                attrs.defaultValue = this.state.selected.length ? this.state.options[this.state.selected[0]].value : '';
            }
            selectElement = (
                <select {...attrs}>
                    {this.state.options.map((opt, index) => {
                        const optionAttributes = {
                            key: index,
                            value: opt.value,
                            disabled: opt.disabled ? true : false
                        };

                        if (multiple) {
                            optionAttributes.selected = opt.selected ? true : false;
                        }

                        return this.state.isOpen ? (
                            <option {...optionAttributes} key={index}>
                                {opt.text || opt.value}
                            </option>
                        ) : null;
                    })}
                </select>
            );
        }

        let label = null;
        if (this.props.label) {
            const labelModifiers = this.props.required ? ['required'] : [];
            let labelClasses = addToArray(this.props.labelClasses, ['label', 'label--select']);
            labelClasses = addModifierClasses(labelModifiers, 'label', labelClasses);
            const labelContentClasses = addToArray(this.props.labelContentClasses, 'label__content');

            label = (
                <label className={styles(labelClasses)} onClick={() => this.handleOpenClose(true)}>
                    <span className={styles(labelContentClasses)}>
                        {this.props.label}
                    </span>
                </label>
            );
        }

        const domElements = (
            <Fragment>
                {!this.props.dropdownOnly ? (
                    <Fragment>
                        {label}
                        <div className={styles(ddClasses)} onClick={this.handleOpenClose} onFocus={this.bindKeyboardEvents} onBlur={this.unbindKeyboardEvents} tabIndex={'0'}>
                            {dropdownIcon || customDropdownIcon ? (<Icon icon={dropdownIcon} customIcon={customDropdownIcon} classes={styles('dropdown__icon')} modifiers={['extra-large']} />) : null}
                            <div className={styles('dropdown__value', 'dropdown__value--filledout')}>
                                {dropdownValue}
                                {dropdownDescription ? <span className={styles('dropdown__value__description', 'tiny', 'c--text-lighter')}>{dropdownDescription}</span> : null}
                            </div>
                            <div className={styles('dropdown__caret', 'align--right')}>
                                <Icon icon={'arrow-down'} modifiers={['small']} />
                            </div>
                        </div>
                    </Fragment>
                ) : null}
                <div className={styles(optionsClasses)}>
                    <ul className={styles('options__list', 'no-list-style', 'b--background', (this.props.collapsed ? 'options__list--collapsed' : ''))} ref={this.refList}>
                        {this.state.options.map((option, index) => option.value === undefined ? null : (
                            <li
                                key={option.id}
                                className={styles(
                                    optionItemClasses
                                        .concat(option.selected ? 'options__item--selected' : [])
                                        .concat(index === this.state.keyboardFocusIndex ? 'options__item--focus' : [])
                                )}
                                onClick={() => this.handleSelection(option)}
                                onMouseEnter={() => this.setKeyboardFocus(index, false)}
                            >
                                {this.props.collapsed ? null : this.state.hasIcons && (option.icon || option.customIcon) ? (
                                    <Icon icon={option.icon} customIcon={option.customIcon} classes={styles('dropdown__icon')} modifiers={['extra-large']} />
                                ) : this.state.hasIcons ? <div className={styles('dropdown__icon')}></div> : null}
                                <div className={styles('dropdown__value')}>
                                    {(this.props.collapsed ? null : option.html) || option.text || option.value}
                                </div>
                            </li>
                        ))}
                    </ul>
                    <div className={styles(optionsArrowClasses)}></div>
                </div>
            </Fragment>
        );

        return (
            <Fragment>
                {selectElement}
                {!this.props.dropdownOnly ? <div className={styles('dropdown')} ref={this.refContainer}>{domElements}</div> : domElements}
                {description}
            </Fragment>
        );
    }
}

Dropdown.propTypes = {
    // properties
    classes: PropTypes.string,
    inputModifiers: PropTypes.arrayOf(PropTypes.string),
    label: PropTypes.string,
    labelClasses: PropTypes.string,
    labelContentClasses: PropTypes.string,
    collapsed: PropTypes.bool,
    max: PropTypes.number,
    description: PropTypes.string,
    separator: PropTypes.string,
    defaultIcon: PropTypes.string,
    isOpen: PropTypes.bool,
    isSelect: PropTypes.bool,
    dropdownOnly: PropTypes.bool,
    required: PropTypes.bool,
    defaultValue: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.any.isRequired,
            text: PropTypes.string,
            selected: PropTypes.bool,
            html: PropTypes.node
        })
    ).isRequired,

    language: PropTypes.string.isRequired,

    // functions
    callback: PropTypes.func.isRequired
};

const mapStateToProps = state => {
    return {
        language: state.global.localization.language
    };
};

export default connect(mapStateToProps, null)(Dropdown);



// WEBPACK FOOTER //
// ./src/components/UI/Dropdown/Dropdown.js